import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '~/components/Button';
import { FilterChips } from '~/components/FilterChips';
import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { FilterDrawer } from '~/modules/settings/components/FilterList';
import { DEFAULT_PAGE_SIZE } from '~/modules/settings/constants';
import { USER_TYPES } from '~/modules/settings/constants/Filter';
import { useFilters } from '~/modules/settings/hooks/useFilters';
import { useSearch } from '~/modules/settings/hooks/useSearch';
import { useSort } from '~/modules/settings/hooks/useSort';
import { UsersFilterForm } from '~/modules/settings/modules/user/components/UserFormFilter/UserFormFilter';
import type { UserListItem } from '~settings/api/lib/schemas';
import { useUserList } from '~settings/api/queries/userList';
import { ListControls } from '~settings/components/ListControls';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { translateOptions } from '~settings/utils/helpers';

import { useUsersColumns } from './hooks/useUsersColumns';

const userEmptyArray: UserListItem[] = [];

export const ListUserPage = () => {
  const { page, setPageNumber, perPage, skip, take } =
    usePagination(DEFAULT_PAGE_SIZE);
  const columns = useUsersColumns();
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { searchTerm } = useSearch();

  const { filterData, drawerOpen, closeDrawer, openDrawer } = useFilters();
  const { sorting, setSorting, sortParams } = useSort();

  const { isLoading, isFetching, isError, userList } = useUserList({
    take,
    skip,
    ...sortParams,
    ...filterData,
    ...(searchTerm && { search: searchTerm }),
  });

  const onClick = (row: UserListItem) => {
    navigate(SETTINGS_ROUTES.USER_DETAIL(row.id));
  };

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }
  if (isError) {
    // eslint-disable-next-line i18next/no-literal-string
    return <section className="py-md">Something went wrong.</section>;
  }

  return (
    <>
      <ListControls
        placeholder={t('user.search')}
        total={userList?.total ?? 0}
        page={page}
        perPage={perPage}
        entity="users"
        onFilterClick={openDrawer}
        setPageNumber={setPageNumber}
        toolbarRightAddon={
          <Button
            asChild
            variant="primary"
            className="shrink-0 self-center"
            size="large"
          >
            <Link to={SETTINGS_ROUTES.USER_CREATE}>{t('user.addLink')}</Link>
          </Button>
        }
        internalFilter={
          <FilterChips
            options={translateOptions(USER_TYPES, t)}
            path="isInternal"
          />
        }
      />
      <Table
        data={userList?.data ?? userEmptyArray}
        columns={columns}
        onRowClick={onClick}
        sort={{
          sorting,
          onSortingChange: setSorting,
        }}
        skeletonLoading={{
          isLoading: isFetching,
          prefillRowsNumber: DEFAULT_PAGE_SIZE,
        }}
      />
      <FilterDrawer open={drawerOpen} onOpenChange={closeDrawer}>
        <UsersFilterForm open={drawerOpen} onApply={closeDrawer} />
      </FilterDrawer>
    </>
  );
};
ListUserPage.displayName = 'ListUserPage';
